/**
 * Sign up · sign in · join by invite · office email check (screenshots/93–97, phone 99; brief §2.18), with the Civic
 * Pass on the right (LanyardBadge). Handoff starter src/auth/AuthPage.tsx, wired to Better Auth through authApi.ts.
 *   /dashboard/signup[?as=office]  Get your Civic Pass (93) · Office account (97)
 *   /dashboard/signin              Welcome back (95); also where the emailed link lands (?token=)
 *   /dashboard/invite/<id>         Join Ward 32 (96): the invite locks the email
 * After the link (or code) the user comes back signed in and onboarding (100–105) starts. Nothing here grants
 * dashboard access: residents are approved, team accounts are created by the Chicago Politics team.
 */
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { ArrowRight, Hash, House, KeyRound, Lock, Mail, MailOpen, Pencil, RotateCw, Ticket } from "lucide-react";
import { passView } from "./civicPass";
import { LanyardBadge } from "./LanyardBadge";
import { AuthField, AuthLayout, CodeBoxes } from "./AuthLayout";
import { canSubmit, initialSignup, mailApps, problems, resendIn, signupReducer, visibleProblems, type AuthMode, type Invite } from "./signupFlow";
import { OFFICE_DOMAIN } from "../onboarding/onboardingFlow";
import type { AuthApi } from "./authApi";

const HEAD: Record<AuthMode, { eyebrow?: string; title: string; sub: string; cta: string }> = {
  signup: { eyebrow: "Chicago City Council, in one place", title: "Get your Civic Pass.", sub: "Meetings, votes and the money behind them. Free for Chicago residents.", cta: "Email me a sign-in link" },
  signin: { title: "Welcome back.", sub: "We’ll email you a sign-in link.", cta: "Email me a sign-in link" },
  invite: { title: "", sub: "", cta: "Accept invite" },
};
const OFFICE_HEAD = { title: "Office account", sub: "Office accounts need a City email.", cta: "Use my City email" };

export function AuthPage({ mode = "signup", invite = null, intendedGroup = null, api, callbackURL = "/dashboard/welcome", now = () => Date.now(), onSwitchMode, initialError = null }: {
  mode?: AuthMode; invite?: Invite | null; intendedGroup?: "office" | null; api: AuthApi; callbackURL?: string; now?: () => number;
  onSwitchMode?: (m: AuthMode) => void; initialError?: string | null;
}) {
  const [s, dispatch] = useReducer(signupReducer, undefined, () => ({ ...initialSignup(mode, invite, intendedGroup), error: initialError, phase: initialError ? "error" as const : "editing" as const }));
  const [, setTick] = useState(0);
  const [showCode, setShowCode] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const err = visibleProblems(s);
  const office = s.intendedGroup === "office" && !s.invite;
  const officeProblem = office && !!err.email && problems(s).email === `Use your ${OFFICE_DOMAIN} email.`;
  const head = s.mode === "invite" && s.invite
    ? { eyebrow: undefined, title: `Join ${s.invite.orgName.replace(/ office$/, "")}.`, sub: `${s.invite.invitedBy} invited you to the ${s.invite.orgName} team.`, cta: "Accept invite" }
    : office ? { eyebrow: undefined, ...OFFICE_HEAD, cta: officeProblem ? OFFICE_HEAD.cta : HEAD.signup.cta } : HEAD[s.mode];

  useEffect(() => { dispatch({ t: "mode", mode }); setShowCode(false); }, [mode]);   // the path changed (switch link, back/forward)
  useEffect(() => { if (s.phase !== "sent") return; const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, [s.phase]);
  useEffect(() => { document.title = `${head.title.replace(/\.$/, "")} · Chicago Politics`; }, [head.title]);
  const wait = resendIn(s, now());

  const view = useMemo(() => passView({
    name: s.name, email: s.email, emailVerified: !!s.invite || s.phase === "verified",
    group: s.invite?.group ?? (s.intendedGroup === "office" ? "office" : null),
    ward: s.invite?.ward ? { number: s.invite.ward, alderpersonName: s.invite.alderpersonName ?? "" } : null,
    invitedBy: s.invite ? s.invite.orgName : null, topics: [], follows: [],
    emailNote: officeProblem ? "needs a City email" : null,
  }), [s.name, s.email, s.phase, s.invite, s.intendedGroup, officeProblem]);

  const send = async () => {
    if (officeProblem) { const el = emailRef.current; el?.focus(); el?.setSelectionRange(0, el.value.indexOf("@") < 0 ? el.value.length : el.value.indexOf("@")); return; }
    dispatch({ t: "submit" });
    if (!canSubmit(s)) return;
    const r = await api.sendLink({ email: s.email.trim(), name: s.name.trim() || undefined, callbackURL: s.invite ? `/dashboard/invite/${encodeURIComponent(s.invite.id)}/accept` : callbackURL });
    dispatch(r.error ? { t: "failed", error: r.error.message } : { t: "sent", at: now() });
  };
  const useCode = async () => { const r = await api.sendCode(s.email.trim()); if (r.error) dispatch({ t: "failed", error: r.error.message }); else setShowCode(true); };
  const verify = async (code: string) => {
    const r = await api.verifyCode(s.email.trim(), code, s.name.trim() || undefined);
    if (r.error) { dispatch({ t: "failed", error: r.error.message }); return; }
    dispatch({ t: "verified" });
    location.assign(s.invite ? `/dashboard/invite/${encodeURIComponent(s.invite.id)}/accept` : callbackURL);
  };
  const passkey = async () => { const r = await api.passkey(); if (r.error) dispatch({ t: "failed", error: r.error.message }); else location.assign(callbackURL); };

  const switchTo = (m: AuthMode) => (onSwitchMode ? onSwitchMode(m) : location.assign(m === "signin" ? "/dashboard/signin" : "/dashboard/signup"));
  const top = s.mode === "signin"
    ? <span className="auth__switch">New here? <button className="link" onClick={() => switchTo("signup")}>Get a pass</button></span>
    : <span className="auth__switch">Have a pass? <button className="link" onClick={() => switchTo("signin")}>Sign in</button></span>;
  const alerts = s.error && <p role="alert" className="auth__error">{s.error}</p>;

  return (
    <AuthLayout top={top} className={`is-${s.mode}`} stage={<LanyardBadge view={view} />}>
      {s.phase !== "sent" ? (
        <form className="auth__body" noValidate onSubmit={(e) => { e.preventDefault(); void send(); }}>
          {head.eyebrow && <p className="auth__eyebrow">{head.eyebrow}</p>}
          <h1 className="auth__title">{head.title}</h1>
          <p className="auth__sub">{head.sub}</p>
          {s.mode !== "signin" && <AuthField id="su-name" label="Your name" value={s.name} autoComplete="name" placeholder="First and last name" error={err.name}
            onChange={(e) => dispatch({ t: "name", name: e.target.value })} onBlur={() => dispatch({ t: "blur", field: "name" })} />}
          <AuthField id="su-email" label="Email" type="email" value={s.email} autoComplete="email" readOnly={!!s.invite}
            error={err.email} note={s.invite ? `Verified by your invite${s.invite.group === "office" ? " · office email" : ""}` : undefined}
            trailing={s.invite ? <Lock size={18} className="auth__lock" aria-hidden /> : undefined} inputRef={emailRef}
            onChange={(e) => dispatch({ t: "email", email: e.target.value })} onBlur={() => dispatch({ t: "blur", field: "email" })} />
          {alerts}
          {showCode ? <CodeEntry onVerify={verify} /> : <>
            <button className="auth__primary" type="submit" disabled={s.phase === "sending"}>{head.cta}<ArrowRight size={18} aria-hidden /></button>
            <div className="auth__alt">
              {!office && <button type="button" onClick={() => void passkey()}><KeyRound size={18} aria-hidden />Use a passkey</button>}
              {s.mode === "signin" ? <button type="button" disabled={!!problems(s).email} onClick={() => void useCode()}><Hash size={18} aria-hidden />Use a 6-digit code</button>
                : office ? <button type="button" onClick={() => dispatch({ t: "asResident" })}><House size={18} aria-hidden />Continue as a resident</button>
                : null}
              {s.mode === "signup" && <a href="/dashboard/invite"><Ticket size={18} aria-hidden />I have an invite</a>}
            </div>
          </>}
        </form>
      ) : (
        <section className="auth__body" aria-labelledby="inbox-title">
          <h1 className="auth__title" id="inbox-title">Check your inbox.</h1>
          <p className="auth__sub">We sent a sign-in link to {s.email}.</p>
          <div className="auth__alt auth__alt--solid">{mailApps(s.email).map((m) => (
            <a key={m.label} href={m.href} target="_blank" rel="noreferrer">{m.label.includes("Outlook") ? <MailOpen size={18} aria-hidden /> : <Mail size={18} aria-hidden />}{m.label}</a>))}</div>
          {alerts}
          {showCode ? <CodeEntry onVerify={verify} /> : <>
            <button className="auth__primary is-quiet" disabled={wait > 0} onClick={() => void send()}>
              {wait > 0 ? `Resend link in 0:${String(wait).padStart(2, "0")}` : "Resend link"}<RotateCw size={18} aria-hidden /></button>
            <div className="auth__alt">
              <button type="button" onClick={() => void useCode()}><Hash size={18} aria-hidden />Use a 6-digit code</button>
              {!s.invite && <button type="button" onClick={() => { dispatch({ t: "changeEmail" }); setShowCode(false); }}><Pencil size={18} aria-hidden />Change email</button>}
            </div>
          </>}
        </section>
      )}
      <p className="auth__fine">By continuing you agree to the <a href="/terms">Terms</a> and <a href="/privacy">Privacy Policy</a>.</p>
      <p className="auth__phone-switch">{top}</p>
    </AuthLayout>
  );
}

/** The 6-digit code (sent by "Use a 6-digit code"): verifies as soon as six digits are in. */
function CodeEntry({ onVerify }: { onVerify: (code: string) => Promise<void> }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form className="auth__code" onSubmit={async (e) => { e.preventDefault(); if (code.length === 6) { setBusy(true); await onVerify(code); setBusy(false); } }}>
      <CodeBoxes value={code} onChange={setCode} label="6-digit code" autoFocus />
      <button className="auth__primary" disabled={code.length !== 6 || busy}>Verify and continue<ArrowRight size={18} aria-hidden /></button>
    </form>
  );
}
